import {
  AltTransportFill18Icon,
  AltTransportOutline18Icon,
  CodeFill18Icon,
  CodeOutline18Icon,
  HistoryFill18Icon,
  HistoryOutline18Icon,
  KlippekortFill18Icon,
  KlippekortOutline18Icon,
  MapFill18Icon,
  MapOutline18Icon,
  PersonFill18Icon,
  PersonOutline18Icon,
  PositionFill18Icon,
  PositionOutline18Icon,
  TracksFill18Icon,
  TracksOutline18Icon,
} from "@vygruppen/spor-icon-react";
import { MeasureOverview } from "features/CenterContent/RoleContent/AlternativeTransport/MeasureOverview/MeasureOverview";
import ISirkView from "features/CenterContent/RoleContent/ISirk/ISirkView";
import Lokleder from "features/CenterContent/RoleContent/Lokleder/Lokleder";
import ReasonCodes from "features/CenterContent/RoleContent/Lokleder/ReasonCodes/ReasonCodes";
import { Personellstyrer } from "features/CenterContent/RoleContent/Personellstyrer/Personellstyrer";
import TrainMap from "features/CenterContent/RoleContent/TrainMap/TrainMap";
import Vaktleder from "features/CenterContent/RoleContent/Vaktleder/Vaktleder";
import Checklist from "features/CenterContent/RoleContent/VehicleChecklist/Checklist";
import VehicleMap from "features/CenterContent/RoleContent/VehicleMap/VehicleMap";
import { HistoryOverview } from "features/History/HistoryOverview";
import { TFunction } from "i18next";
import { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { UserRole } from "shared/types/roles";
import { isNotNullish } from "shared/utils/objectUtils";
import styled, { useTheme } from "styled-components";

const TabsWrapper = styled.div`
  height: 100%;
  overflow: hidden;
  position: relative;
`;

const TrainMapTabs = () => (
  <TabsWrapper>
    <TrainMap />
  </TabsWrapper>
);

const ISirkTabs = () => (
  <TabsWrapper>
    <ISirkView />
  </TabsWrapper>
);

const VehicleMapTabs = () => (
  <TabsWrapper>
    <VehicleMap />
  </TabsWrapper>
);

const HistoryTabs = () => (
  <TabsWrapper>
    <HistoryOverview />
  </TabsWrapper>
);

const MeasureOverviewTabs = () => (
  <TabsWrapper>
    <MeasureOverview />
  </TabsWrapper>
);

const ChecklistsTabs = () => (
  <TabsWrapper>
    <Checklist />
  </TabsWrapper>
);

export const ReasonCodesTabs = () => {
  const theme = useTheme();
  return (
    <TabsWrapper
      style={{
        backgroundColor: theme.colorBackgroundMain,
        border: `1px solid ${theme.colorOutlineSecondary}`,
        color: theme.colorTextMain,
        borderRadius: "6px",
      }}
    >
      <ReasonCodes />
    </TabsWrapper>
  );
};

export enum TabIds {
  START = "start",
  TRAINMAP = "togkart",
  GPSMAP = "gpskart",
  REASONCODES = "reasoncodes",
  CHECKLIST = "kvitteringsliste",
  HISTORY = "oversikt",
  MEASUREOVERVIEW = "tiltak",
}

type TabsInfo = {
  id: TabIds;
  name: string;
  content: ReactElement;
  iconActive: ReactElement;
  iconInactive: ReactElement;
  alertSemantics?: "alarm" | "warning";
  alertCount?: number;
};

export const getTrainMapName = (
  translate: TFunction<"translation", undefined>,
) => translate("Togkart");

export const useTabsForRole = (role: UserRole): TabsInfo[] | null => {
  const { t } = useTranslation();

  const LoklederInfo = {
    id: TabIds.START,
    name: "Lokleder",
    content: <Lokleder />,
    iconActive: <PersonFill18Icon />,
    iconInactive: <PersonOutline18Icon />,
  };

  const TrainMapTabsInfo = {
    id: TabIds.TRAINMAP,
    name: getTrainMapName(t),
    content: <TrainMapTabs />,
    iconActive: <MapFill18Icon />,
    iconInactive: <MapOutline18Icon />,
  };

  const TrainMapTabsInfoVaktleder = {
    ...TrainMapTabsInfo,
    id: TabIds.START,
    content: <Vaktleder />,
  };

  const VehicleMapTabsInfo = {
    id: TabIds.GPSMAP,
    name: "GPS-kart",
    content: <VehicleMapTabs />,
    iconActive: <PositionFill18Icon />,
    iconInactive: <PositionOutline18Icon />,
  };

  const TransportlederInfo = {
    ...TrainMapTabsInfo,
    id: TabIds.START,
  };

  const VehicleMapTabsInfoMateriellstyrer = {
    ...VehicleMapTabsInfo,
    id: TabIds.START,
  };

  const MeasureOverviewInfo = {
    id: TabIds.MEASUREOVERVIEW,
    name: "Tiltak",
    content: <MeasureOverviewTabs />,
    iconActive: <TracksFill18Icon />,
    iconInactive: <TracksOutline18Icon />,
  };

  const PersonellstyrerInfo = {
    id: TabIds.START,
    name: "Personell",
    content: <Personellstyrer />,
    iconActive: <PersonFill18Icon />,
    iconInactive: <PersonOutline18Icon />,
  };

  const ReasonCodesTabsInfo = {
    id: TabIds.REASONCODES,
    name: "Årsakskoder",
    content: <ReasonCodesTabs />,
    iconActive: <CodeFill18Icon />,
    iconInactive: <CodeOutline18Icon />,
  };

  const ISirkTabsInfo = {
    id: TabIds.START,
    name: "I-Sirk",
    content: <ISirkTabs />,
    iconActive: <AltTransportFill18Icon />,
    iconInactive: <AltTransportOutline18Icon />,
  };

  const historyTabName = t("Oversikt");

  const HistoryTabsInfo = {
    id: TabIds.HISTORY,
    name: historyTabName,
    content: <HistoryTabs />,
    iconActive: <HistoryFill18Icon />,
    iconInactive: <HistoryOutline18Icon />,
  };

  const ChecklistsTabsInfo = {
    id: TabIds.CHECKLIST,
    name: "Kvitteringer",
    content: <ChecklistsTabs />,
    iconActive: <KlippekortFill18Icon />,
    iconInactive: <KlippekortOutline18Icon />,
  };

  switch (role) {
    case UserRole.lokleder1:
    case UserRole.lokleder2:
      return [
        LoklederInfo,
        HistoryTabsInfo,
        TrainMapTabsInfo,
        VehicleMapTabsInfo,
        ChecklistsTabsInfo,
      ];

    case UserRole.personellstyrerkond:
    case UserRole.personellstyrerlok:
    case UserRole.personellstyrervest:
    case UserRole.personellstyrerekstra:
      return [PersonellstyrerInfo, HistoryTabsInfo, VehicleMapTabsInfo];

    case UserRole.materiellstyrerlokal:
    case UserRole.materiellstyrerregion:
    case UserRole.okm:
      return [
        VehicleMapTabsInfoMateriellstyrer,
        HistoryTabsInfo,
        TrainMapTabsInfo,
        ChecklistsTabsInfo,
      ];

    case UserRole.transportleder:
      return [
        TransportlederInfo,
        HistoryTabsInfo,
        VehicleMapTabsInfo,
        ReasonCodesTabsInfo,
        ChecklistsTabsInfo,
      ];

    case UserRole.vaktleder:
      return [
        MeasureOverviewInfo,
        TrainMapTabsInfoVaktleder,
        HistoryTabsInfo,
        VehicleMapTabsInfo,
        ReasonCodesTabsInfo,
      ].filter(isNotNullish);

    case UserRole.driftledningen:
      return [TrainMapTabsInfoVaktleder, HistoryTabsInfo];

    case UserRole.trafikkplanlegger60:
    case UserRole.trafikkplanlegger63:
    case UserRole.oks:
      return [
        MeasureOverviewInfo,
        ISirkTabsInfo,
        HistoryTabsInfo,
        TrainMapTabsInfo,
        VehicleMapTabsInfo,
        ReasonCodesTabsInfo,
      ].filter(isNotNullish);

    case UserRole.trafikkplanlegger:
      // Deprecated role
      return null;
  }
};
